import {tx, ptx, usePageText} from '../lib/page-text';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api-client';
import { getIceServers } from '../lib/ice';

interface Props {
  streamKey: string;
  streamId: string;
  autoPlay?: boolean;
  /** Start muted. On by default: the broadcaster monitoring their own feed
   *  with audio on is a feedback loop straight back into their microphone. */
  muted?: boolean;
  /** Keep trying while the broadcast has not started yet. WHEP answers 400 for
   *  a stream nobody is publishing, which is the normal state in the seconds
   *  between opening the page and hitting "Go live" — without this the monitor
   *  fails once and sits there dead. */
  autoRetry?: boolean;
  /** The stream is live by the API's account, so an empty player means the
   *  picture is on its way — not that nobody is broadcasting. */
  streamLive?: boolean;
}

const WHEP_ENDPOINT = `${window.location.origin}/rtc/v1/whep/`;
const RETRY_MIN_MS = 2_000;
const RETRY_MAX_MS = 10_000;

/**
 * WHEP (WebRTC-HTTP Egress Protocol) viewer — the pull-side counterpart to
 * WHIP. Negotiates a receive-only RTCPeerConnection with SRS over one HTTP POST
 * that exchanges SDP; media flows back over UDP 8000. Glass-to-glass lands
 * around a second, against ~12s for the HLS ladder.
 *
 * Browser flow:
 *   1. new RTCPeerConnection({ iceServers: [STUN] })
 *   2. addTransceiver('audio' | 'video', { direction: 'recvonly' })
 *   3. createOffer + setLocalDescription, wait for ICE gathering
 *   4. POST the offer to /rtc/v1/whep/?app=live&stream=<key>
 *   5. setRemoteDescription with the answer; ontrack attaches the stream
 */
export function WhepPlayer({ streamKey, streamId, autoPlay = true, muted: mutedProp = true, autoRetry = false, streamLive = false }: Props) {
  usePageText();
  const videoRef = useRef<HTMLVideoElement>(null);
  const session = useRef(0);
  const cleanup = useRef<() => void>(() => {});
  const [restart, setRestart] = useState(0);
  const [state, setState] = useState<'idle' | 'connecting' | 'waiting' | 'connected' | 'playing' | 'error'>('idle');
  const [error, setError] = useState('');
  const [playBlocked, setPlayBlocked] = useState(false);
  const [muted, setMuted] = useState(mutedProp);
  const [stats, setStats] = useState<{ kbps: number; rtt: number | null; resolution: string }>({ kbps: 0, rtt: null, resolution: '—' });
  const connect = () => { cleanup.current(); setRestart(n => n + 1); };
  const play = () => videoRef.current?.play().then(() => setPlayBlocked(false)).catch(() => setPlayBlocked(true));

  useEffect(() => {
    if (!autoPlay && !restart) return;
    let disposed = false, attempt = 0, delay = RETRY_MIN_MS;
    let pc: RTCPeerConnection | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let statsTimer: ReturnType<typeof setInterval> | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | null = null;
    const generation = ++session.current;
    const close = () => {
      attempt++;
      controller?.abort(); controller = null;
      clearInterval(statsTimer); clearTimeout(timeout);
      const previous = pc; pc = null; previous?.close();
      if (videoRef.current) videoRef.current.srcObject = null;
    };
    const retry = (message: string) => {
      if (disposed || session.current !== generation) return;
      close(); setState('error'); setError(message);
      if (autoRetry && !retryTimer) {
        retryTimer = setTimeout(() => { retryTimer = undefined; void begin(); }, delay);
        delay = Math.min(RETRY_MAX_MS, delay * 1.5);
      }
    };
    async function begin() {
      close();
      const ticket = attempt;
      const current = () => !disposed && generation === session.current && ticket === attempt;
      setState('connecting'); setError(''); setPlayBlocked(false);
      controller = new AbortController();
      timeout = setTimeout(() => retry('The preview has not received a picture. Reconnecting…'), 25000);
      try {
        // Same tenant-scoped access grant as the viewer link; never make private media public.
        // Mint on reconnect so long-running monitors never reuse an expired grant.
        const [grant, iceServers] = await Promise.all([
          api.post<{ token: string }>(`/me/streams/${encodeURIComponent(streamId)}/invite`, { minutes: 5 }, { signal: controller.signal }),
          getIceServers(streamKey),
        ]);
        if (!current()) return;
        if (!grant.data.token) throw Error('Could not authorize the stream preview.');
        const peer = new RTCPeerConnection({ iceServers, bundlePolicy: 'max-bundle' }); pc = peer;
        peer.addTransceiver('audio', { direction: 'recvonly' });
        peer.addTransceiver('video', { direction: 'recvonly' });
        const media = new MediaStream();
        peer.ontrack = event => {
          if (!current()) return;
          if (!media.getTracks().includes(event.track)) media.addTrack(event.track);
          if (videoRef.current) { videoRef.current.srcObject = media; if (autoPlay) void play(); }
        };
        peer.onconnectionstatechange = () => {
          if (!current()) return;
          if (peer.connectionState === 'failed' || peer.connectionState === 'disconnected') retry('Preview connection interrupted. Reconnecting…');
          if (peer.connectionState === 'connected') setState(s => s === 'playing' ? s : 'connected');
        };
        await peer.setLocalDescription(await peer.createOffer());
        await gatherIce(peer);
        if (!current()) return;
        const query = new URLSearchParams({ app: 'live', stream: streamKey, t: grant.data.token });
        const response = await fetch(`${WHEP_ENDPOINT}?${query}`, {
          method: 'POST', signal: controller!.signal,
          headers: { 'Content-Type': 'application/sdp' }, body: peer.localDescription?.sdp ?? '',
        });
        if (!current()) return;
        if (!response.ok) throw Error(response.status === 400 || response.status === 404
          ? 'The media server could not open this preview. Retrying with fresh stream access…'
          : `Could not open the preview (${response.status}).`);
        const answer = await response.text();
        if (!current()) return;
        await peer.setRemoteDescription({ type: 'answer', sdp: answer });
        if (!current()) return;
        let previousBytes = 0, previousFrames = 0, quiet = 0;
        statsTimer = setInterval(async () => {
          try {
            const reports = await peer.getStats();
            if (!current()) return;
            let bytes = 0, frames = 0, resolution = '—', rtt: number | null = null;
            reports.forEach((r: any) => {
              if (r.type === 'inbound-rtp' && r.kind === 'video') {
                bytes += r.bytesReceived ?? 0; frames += r.framesDecoded ?? 0;
                if (r.frameWidth && r.frameHeight) resolution = `${r.frameWidth}×${r.frameHeight}`;
              }
              if (r.type === 'candidate-pair' && r.state === 'succeeded' && r.currentRoundTripTime != null) rtt = Math.round(r.currentRoundTripTime * 1000);
            });
            setStats({ kbps: previousBytes ? Math.max(0, Math.round((bytes - previousBytes) * 8 / 1000)) : 0, rtt, resolution });
            if (frames > previousFrames) { quiet = 0; delay = RETRY_MIN_MS; clearTimeout(timeout); setState('playing'); }
            else if (++quiet >= 12) retry('The preview stopped receiving video. Reconnecting…');
            previousBytes = bytes; previousFrames = frames;
          } catch { /* A closed connection is handled by the lifecycle above. */ }
        }, 1000);
      } catch (e: any) { if (current()) retry(e?.response?.status === 403 ? 'You do not have permission to preview this stream.' : e?.message ?? 'Could not load the preview.'); }
    }
    cleanup.current = () => { disposed = true; clearTimeout(retryTimer); close(); };
    void begin();
    return () => cleanup.current();
  }, [streamKey, streamId, autoPlay, autoRetry, restart]);

  const pill = {
    idle:       { label: 'Not watching',                   color: 'var(--text-mute)' },
    connecting: { label: 'Connecting…',                    color: 'var(--text-mute)' },
    waiting:    { label: streamLive ? 'Loading the picture…' : 'Not broadcasting yet', color: 'var(--text-mute)' },
    connected:  { label: 'Waiting for the picture…',       color: 'var(--text-mute)' },
    playing:    { label: '● Live — this is what viewers see', color: 'var(--danger)' },
    error:      { label: 'Playback problem',               color: 'var(--danger)' },
  }[state];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', background: '#000', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        <video ref={videoRef} controls={state === 'playing' || playBlocked} playsInline muted={muted} autoPlay={autoPlay}
               style={{ width: '100%', height: '100%', display: 'block' }} />
        {playBlocked && <button className="btn primary" style={{ position: 'absolute', top: '45%', left: '35%' }} onClick={() => void play()}>{tx("Play preview")}</button>}
        {state === 'idle' && (
          <button className="btn primary lg" onClick={connect}
                  style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}>{tx("▶  Watch live")}</button>
        )}
        {(state === 'waiting' || state === 'connecting' || state === 'connected') && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--text-mute)', fontSize: 13, textAlign: 'center', padding: 16, pointerEvents: 'none' }}>
            {state === 'connecting' ? tx("Connecting…") : streamLive ? tx("Your broadcast is starting — the picture appears here in a few seconds.") : tx("Nothing is being broadcast yet. When you go live, the picture appears here by itself.")}
          </div>
        )}
        {muted && state === 'playing' && (
          <button className="btn sm" onClick={() => setMuted(false)}
                  style={{ position: 'absolute', bottom: 48, left: 12 }}>{tx("🔇 Unmute")}</button>
        )}
      </div>
      <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', fontSize: 12, flexWrap: 'wrap', gap: 8 }}>
        <span style={{ color: pill.color, fontWeight: 600 }}>{tx(pill.label)}</span>
        {state !== 'idle' && <button className="btn outline sm" onClick={connect}>{tx("Reconnect preview")}</button>}
        {state === 'playing' && (
          <span className="muted mono" style={{ fontSize: 11 }} title={tx("Resolution · bitrate · round-trip time")}>{ptx("{v0} · {v1} kbps {v2}", {v0: (stats.resolution), v1: (stats.kbps), v2: (stats.rtt != null && ptx("· {v0} ms", {v0: (stats.rtt)}))})}</span>
        )}
      </div>
      {state === 'error' && (
        <div style={{ fontSize: 12, color: 'var(--danger)' }}>
          {error} <button className="btn outline sm" style={{ marginLeft: 8 }} onClick={connect}>{tx("Retry")}</button>
        </div>
      )}
    </div>
  );
}

/** WHIP/WHEP post a fully-gathered offer rather than trickling candidates, so
 *  the POST waits on gathering — with a ceiling, because a host that never
 *  finishes gathering would otherwise hang the connect forever. */
function gatherIce(pc: RTCPeerConnection, timeoutMs = 2000): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => { clearTimeout(timer); resolve(); };
    const timer = setTimeout(done, timeoutMs);
    pc.addEventListener('icegatheringstatechange', () => {
      if (pc.iceGatheringState === 'complete') done();
    });
  });
}
